import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { pushToDataLayer } from '../../../../../services/gtmService';

import { RESERVATION_AD_ID_LOCAL_STORAGE_KEY } from './ReservationTracking.constants';

import {
  TRUSTAP_CODE_CANCELLED,
  TRUSTAP_CODE_QUERY_KEY,
  TRUSTAP_STATUS_INTERNAL_SERVER_ERROR,
  TRUSTAP_STATUS_OK,
  TRUSTAP_STATUS_QUERY_KEY,
} from '../../constants/trustap';

import type { ReservationTrackingArgs } from './ReservationTracking.typed';
import { RESERVATION_STATUS } from '../../types';

const useReservationTracking = (args: ReservationTrackingArgs) => {
  const { adId, reservationStatus } = args;
  const { query } = useRouter();

  useEffect(() => {
    const reservedAdId = getCachedReservedAdId();

    if (reservedAdId !== adId) {
      clearCachedReservedAdId();
      return;
    }

    if (
      query[TRUSTAP_STATUS_QUERY_KEY] === TRUSTAP_STATUS_OK &&
      query[TRUSTAP_CODE_QUERY_KEY] !== TRUSTAP_CODE_CANCELLED
    ) {
      pushToDataLayer({
        event: 'reservation_disabled',
      });
    } else if (
      (query[TRUSTAP_STATUS_QUERY_KEY] === TRUSTAP_STATUS_OK &&
        query[TRUSTAP_CODE_QUERY_KEY] === TRUSTAP_CODE_CANCELLED) ||
      query[TRUSTAP_STATUS_QUERY_KEY] === TRUSTAP_STATUS_INTERNAL_SERVER_ERROR
    ) {
      pushToDataLayer({
        event: 'reservation_enabled',
      });
    } else {
      /**
       * In the event that the user does not return to ad details from the checkout, we use the reservation status of the ad, if any,
       * rather than Trustap queries.
       *
       * Those queries will only be available based on certain actions during the checkout process, and upon returning directly to ad details.
       */
      pushToDataLayer({
        event: `reservation_${
          reservationStatus === RESERVATION_STATUS.ENABLED
            ? 'enabled'
            : 'disabled'
        }`,
      });
    }

    clearCachedReservedAdId();

    function getCachedReservedAdId() {
      try {
        const rawId = window.localStorage.getItem(
          RESERVATION_AD_ID_LOCAL_STORAGE_KEY,
        );

        if (!rawId) return null;

        const parsedId = Number.parseInt(rawId);

        return Number.isNaN(parsedId) ? null : parsedId;
      } catch {
        return null;
      }
    }

    function clearCachedReservedAdId() {
      try {
        window.localStorage.removeItem(RESERVATION_AD_ID_LOCAL_STORAGE_KEY);
      } catch {}
    }
  }, [query, reservationStatus, adId]);
};

export { useReservationTracking };
